import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button, Divider } from '@material-ui/core';

import { ASSET_TYPE } from 'src/data/asset';
import { BRIEF_TYPE } from 'src/data/brief';
import { ContentSensitivityInfo } from 'src/components/ContentSensitivityInfo';
import { BLOCKED_REASON } from 'src/data/common';
import { GENERATION_TOOL } from 'src/data/generationTool';
import { TEXT_ANNOTATION_TYPE } from 'src/data/annotation';
import { Header } from './components/Header';
import { TextEditor } from './components/TextEditor';
import { Histograms } from './components/Histograms';
import { Annotation } from './components/Annotation';
import { MainAnnotations } from './components/MainAnnotations';
import { BlockedMessage } from './components/BlockedMessage';
import { DetectedBrandTerms } from './components/DetectedBrandTerms';
import { useEditorMarks, usePrepareDetectedBrandTerms } from './services';
import { TEXT_EDITOR_TYPE } from './const';
import { useStyles } from './useStyles';

const scoreIsNotAppealing = (score) => score < 60;

export const TextDetailsPanel = ({
  className,
  briefType,
  assetType,
  editorContent,
  onEditorContentChange,
  scoreData,
  histogramsData,
  annotationsData,
  sensitivityData,
  disabled,
  displayTextEditor,
  textEditorType,
  displayInteractiveAnnotations,
  onUseAnnotationEntry,
  blockedReason,
  locked,
  readOnly,
  onUpgradeClick,
  onUndoClick,
  undoDisabled,
  displayScoringMethod,
  scoringMethodName,
  secondaryScoringMethodName,
  onScoringMethodClick,
  generationTool,
  requireChangesConfirmation,
  onSaveChanges,
  onCancelChanges,
  campaignKeywords,
  displayDetectedBrandTerms,
  detectedBrandTermsData,
  onApplyDetectedBrandTerm,
  onEditBrandTerm,
  editorRef,
  customerHasAccessToPerformance,
  onBusinessBookDemoClick,
}) => {
  const styles = useStyles();
  const noneAppeal = scoreIsNotAppealing(scoreData.score);
  const annotationsVisible = annotationsData.loading || annotationsData.annotations.length > 0;

  const editorMarks = useEditorMarks(detectedBrandTermsData);
  const preparedBrandTerms = usePrepareDetectedBrandTerms(detectedBrandTermsData);

  if (secondaryScoringMethodName) {
    const headlineTypeAnnotation = annotationsData.annotations.find(
      (annotation) => annotation.type === TEXT_ANNOTATION_TYPE.headlineType
    );
    if (headlineTypeAnnotation) {
      headlineTypeAnnotation.value = [{ name: secondaryScoringMethodName }];
    } else {
      // inject headline type into main annotations section
      annotationsData.annotations.unshift({
        type: TEXT_ANNOTATION_TYPE.headlineType,
        value: [{ name: secondaryScoringMethodName }],
      });
    }
  }

  return (
    <div
      className={cx(styles.root, className)}
      data-testid="TextDetailsPanel"
    >
      <div className={styles.headerWrapper}>
        <Header
          className={styles.header}
          briefType={briefType}
          score={scoreData.score}
          basedOnValue={scoreData.basedOn}
          loading={scoreData.loading}
          blocked={!!blockedReason}
          locked={locked}
          readOnly={readOnly}
          displayScoringMethod={displayScoringMethod}
          scoringMethodName={scoringMethodName}
          onScoringMethodClick={onScoringMethodClick}
        />

        {displayTextEditor && editorContent != null && (
          <div className={styles.textEditorWrapper}>
            <TextEditor
              className={styles.textEditor}
              assetType={assetType}
              content={editorContent}
              onChange={onEditorContentChange}
              disabled={disabled}
              editorType={textEditorType}
              readOnly={readOnly}
              marks={editorMarks}
              hideMarks={requireChangesConfirmation}
              editorRef={editorRef}
            />

            {requireChangesConfirmation && !readOnly && (
              <div className={styles.editingActions}>
                <Button
                  onClick={onCancelChanges}
                  disabled={disabled}
                  variant="text"
                  color="primary"
                  size="small"
                >
                  Cancel
                </Button>

                <Button
                  onClick={onSaveChanges}
                  disabled={disabled}
                  variant="contained"
                  color="primary"
                  size="small"
                >
                  Save changes
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.body}>
        <>
          <Histograms
            className={styles.histograms}
            score={scoreData.score}
            dataAge={histogramsData.age}
            dataGender={histogramsData.gender}
            loading={histogramsData.loading}
            noneAppeal={noneAppeal}
          />

          {campaignKeywords?.length > 0 && (
            <>
              <Divider className={styles.divider} />
              <Annotation
                type={TEXT_ANNOTATION_TYPE.campaignKeywords}
                values={campaignKeywords}
              />
            </>
          )}

          <Divider className={styles.divider} />

          {annotationsVisible && (
            <>
              <MainAnnotations
                className={styles.mainAnnotations}
                annotations={annotationsData.annotations}
                loading={annotationsData.loading}
                displayInteractive={displayInteractiveAnnotations}
                onUseAnnotationEntry={onUseAnnotationEntry}
                generationTool={generationTool}
              />

              <Divider className={styles.divider} />
            </>
          )}

          {displayDetectedBrandTerms && (
            <>
              <DetectedBrandTerms
                className={styles.detectedBrandTerms}
                items={preparedBrandTerms.items}
                onApplyDetectedBrandTerm={onApplyDetectedBrandTerm}
                onEditBrandTerm={onEditBrandTerm}
                loading={preparedBrandTerms.loading}
                disabled={disabled}
                customerHasAccessToPerformance={customerHasAccessToPerformance}
                onBusinessBookDemoClick={onBusinessBookDemoClick}
              />

              <Divider className={styles.divider} />
            </>
          )}

          {sensitivityData && (
            <ContentSensitivityInfo
              className={styles.contentSensitivity}
              assetType={assetType}
              sensitivity={sensitivityData.sensitivity}
              loading={sensitivityData.loading}
              horizontalLayout
              policyTitle
            />
          )}
        </>

        {!!blockedReason && (
          <div className={styles.blurred}>
            <BlockedMessage
              className={styles.blockedMessage}
              blockedReason={blockedReason}
              onUpgradeClick={onUpgradeClick}
              onUndoClick={onUndoClick}
              undoDisabled={undoDisabled}
            />
          </div>
        )}
      </div>
    </div>
  );
};

TextDetailsPanel.propTypes = {
  className: PropTypes.string,
  briefType: PropTypes.oneOf(Object.values(BRIEF_TYPE)),
  assetType: PropTypes.oneOf(Object.values(ASSET_TYPE)).isRequired,
  editorContent: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape()), PropTypes.string]),
  onEditorContentChange: PropTypes.func,
  scoreData: PropTypes.shape({
    score: PropTypes.number,
    basedOn: PropTypes.number,
    loading: PropTypes.bool,
  }).isRequired,
  histogramsData: PropTypes.shape({
    age: PropTypes.arrayOf(PropTypes.shape()),
    gender: PropTypes.arrayOf(PropTypes.shape()),
    loading: PropTypes.bool,
  }).isRequired,
  annotationsData: PropTypes.shape({
    annotations: PropTypes.arrayOf(PropTypes.shape({})),
    loading: PropTypes.bool,
  }).isRequired,
  sensitivityData: PropTypes.shape({
    sensitivity: PropTypes.shape({}),
    loading: PropTypes.bool,
  }),
  disabled: PropTypes.bool,
  displayTextEditor: PropTypes.bool,
  textEditorType: PropTypes.oneOf(Object.values(TEXT_EDITOR_TYPE)),
  displayInteractiveAnnotations: PropTypes.bool,
  onUseAnnotationEntry: PropTypes.func,
  blockedReason: PropTypes.oneOf(Object.values(BLOCKED_REASON)),
  locked: PropTypes.bool,
  readOnly: PropTypes.bool,
  onUpgradeClick: PropTypes.func,
  onUndoClick: PropTypes.func,
  undoDisabled: PropTypes.bool,
  displayScoringMethod: PropTypes.bool,
  scoringMethodName: PropTypes.string,
  secondaryScoringMethodName: PropTypes.string,
  onScoringMethodClick: PropTypes.func,
  generationTool: PropTypes.oneOf(Object.values(GENERATION_TOOL)),
  requireChangesConfirmation: PropTypes.bool,
  onSaveChanges: PropTypes.func,
  onCancelChanges: PropTypes.func,
  campaignKeywords: PropTypes.array,
  displayDetectedBrandTerms: PropTypes.bool,
  detectedBrandTermsData: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({})),
    loading: PropTypes.bool,
  }),
  onApplyDetectedBrandTerm: PropTypes.func,
  onEditBrandTerm: PropTypes.func,
  editorRef: PropTypes.shape(),
  customerHasAccessToPerformance: PropTypes.bool,
  onBusinessBookDemoClick: PropTypes.func,
};

TextDetailsPanel.defaultProps = {
  className: null,
  briefType: null,
  editorContent: null,
  onEditorContentChange: null,
  sensitivityData: null,
  disabled: false,
  displayTextEditor: false,
  textEditorType: TEXT_EDITOR_TYPE.markedEditor,
  displayInteractiveAnnotations: false,
  onUseAnnotationEntry: null,
  blockedReason: null,
  locked: false,
  readOnly: false,
  onUpgradeClick: null,
  onUndoClick: null,
  undoDisabled: false,
  displayScoringMethod: false,
  scoringMethodName: null,
  secondaryScoringMethodName: null,
  onScoringMethodClick: null,
  generationTool: null,
  requireChangesConfirmation: false,
  onSaveChanges: null,
  onCancelChanges: null,
  campaignKeywords: null,
  displayDetectedBrandTerms: false,
  detectedBrandTermsData: null,
  onApplyDetectedBrandTerm: null,
  onEditBrandTerm: null,
  editorRef: null,
  customerHasAccessToPerformance: false,
  onBusinessBookDemoClick: null,
};
